import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import * as L from "lucide-react";

/** Promises Rammy thinks it found in the user's mail. Nothing here is real
 *  yet — a suggestion only becomes a promise when the user says yes. */
export function PromiseSuggestions({ suggestions, onResolved }) {
  const owe = suggestions?.you_owe || [];
  const waiting = suggestions?.waiting_on || [];
  if (!owe.length && !waiting.length) return null;

  return (
    <div className="space-y-3" data-testid="home-promise-suggestions">
      {owe.length > 0 && (
        <SuggestionGroup
          label="Did you promise this?"
          icon={L.MailQuestion}
          items={owe}
          onResolved={onResolved}
        />
      )}
      {waiting.length > 0 && (
        <SuggestionGroup
          label="Someone promised you this"
          icon={L.Inbox}
          items={waiting}
          onResolved={onResolved}
        />
      )}
      <div className="px-1 text-[11px] text-[var(--rm-text-faint)] leading-relaxed" data-testid="promise-privacy-note">
        Rammy read your mail to spot these. We don&rsquo;t store your emails &mdash; only the promise.
      </div>
    </div>
  );
}

function SuggestionGroup({ label, icon: Icon, items, onResolved }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.06em] text-[var(--rm-text-faint)] px-1">
        <Icon size={13}/> {label}
      </div>
      {items.map(s => <SuggestionCard key={s.id} s={s} onResolved={onResolved}/>)}
    </div>
  );
}

function SuggestionCard({ s, onResolved }) {
  const [busy, setBusy] = useState(false);
  const [gone, setGone] = useState(false);

  const accept = async () => {
    setBusy(true);
    try {
      await api.post(`/promises/suggestions/${s.id}/accept`, {});
      setGone(true);
      onResolved?.();
      toast.success("Added to your promises");
    } catch { toast.error("Couldn't add that — try again."); }
    finally { setBusy(false); }
  };
  const reject = async () => {
    setBusy(true);
    try {
      await api.post(`/promises/suggestions/${s.id}/reject`);
      setGone(true);
      onResolved?.();
    } catch { toast.error("Couldn't dismiss that — try again."); }
    finally { setBusy(false); }
  };

  if (gone) return null;

  return (
    <div
      className="rounded-2xl border border-dashed border-[var(--rm-border)] bg-[var(--rm-surface)] p-5"
      data-testid={`promise-suggestion-${s.id}`}
    >
      <div className="text-[15px] leading-snug" data-testid={`promise-suggestion-title-${s.id}`}>
        {s.title}
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] font-mono text-[var(--rm-text-faint)]">
        {s.who && <span>{s.direction === "you_owe" ? `for ${s.who}` : `from ${s.who}`}</span>}
        <span data-testid={`promise-suggestion-due-${s.id}`}>
          {dueLabel(s.due_at)}{s.soft_due ? " · no date given" : ""}
        </span>
      </div>
      {s.quote && (
        <div className="mt-2.5 text-[13px] text-[var(--rm-text-muted)] leading-relaxed border-l-2 border-[var(--rm-border)] pl-3">
          &ldquo;{s.quote}&rdquo;
        </div>
      )}
      {s.subject && (
        <div className="mt-1.5 text-[11px] text-[var(--rm-text-faint)] truncate">
          {s.subject}
        </div>
      )}
      <div className="mt-4 flex gap-2">
        <button
          type="button"
          onClick={accept}
          disabled={busy}
          data-testid={`promise-suggestion-accept-${s.id}`}
          className="rounded-full px-3.5 py-1.5 text-[12px] font-medium bg-[var(--rm-ink-900)] text-white hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          Yes, keep it
        </button>
        <button
          type="button"
          onClick={reject}
          disabled={busy}
          data-testid={`promise-suggestion-reject-${s.id}`}
          className="rounded-full px-3.5 py-1.5 text-[12px] font-medium border border-[var(--rm-border)] text-[var(--rm-text-muted)] hover:text-[var(--rm-text)] transition-colors disabled:opacity-50"
        >
          Not a promise
        </button>
      </div>
    </div>
  );
}

function dueLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const mins = Math.round((d - new Date()) / 60000);
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  if (mins < 0) return `due ${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
  if (mins < 60 * 12) return `by ${time}`;
  if (mins < 60 * 36) return `by tomorrow ${time}`;
  return `by ${d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}`;
}

export default PromiseSuggestions;
